import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/common/Button';
import restauragroLogo from '../assets/restauragro-logo.png';
import loginHero from '../assets/login-seedling-hero.jpg';
import toast from 'react-hot-toast';

const benefits = [
  'Quadros Kanban, tarefas e prazos em um só lugar',
  'Times organizados por área de trabalho e projeto',
  'Controle financeiro e orçamentário integrado',
];

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
      toast.success('Bem-vindo(a) de volta!');
    } catch {
      toast.error('E-mail ou senha inválidos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen w-full lg:grid-cols-[1.05fr_1fr]">
      <section
        className="relative hidden flex-col justify-between overflow-hidden px-12 py-12 text-white lg:flex"
        style={{ backgroundImage: `url(${loginHero})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/35 to-black/70" />

        <div className="relative z-10 flex items-center gap-3">
          <div className="rounded-lg bg-white p-1.5 shadow-sm">
            <img src={restauragroLogo} alt="RestaurAgro" className="h-12 w-12 object-contain" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[#9FD6B4]">Plataforma de gestão</p>
            <p className="text-sm text-white/80">de projetos e times</p>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <h1 className="text-3xl font-bold leading-tight">
            Restaurando o Presente.<br />
            <span className="text-[#9FD6B4]">Conservando o Futuro.</span>
          </h1>
          <p className="mt-4 text-white/80">
            Conciliamos produção responsável, manejo sustentável dos recursos naturais e restauração ecológica e produtiva.
          </p>
          <ul className="mt-8 space-y-3">
            {benefits.map(benefit => (
              <li key={benefit} className="flex items-start gap-3">
                <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-[#9FD6B4]" />
                <span className="text-white/90">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative z-10 text-sm text-white/70">© {new Date().getFullYear()} RestaurAgro. Todos os direitos reservados.</p>
      </section>

      <section className="flex flex-col items-center justify-center bg-[#F7F9FF] px-4 py-12">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center lg:hidden">
            <img src={restauragroLogo} alt="RestaurAgro" className="mx-auto h-16 w-16 object-contain" />
          </div>

          <div className="rounded-2xl border border-[#D0D4E4] bg-white p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-[#323338]">Entrar</h2>
            <p className="mt-1 text-sm text-[#676879]">Acesse sua conta para continuar</p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <label htmlFor="email" className="mb-1 block text-sm font-medium text-[#323338]">E-mail</label>
                <div className="relative">
                  <Mail size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#676879]" />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="w-full rounded-lg border border-[#D0D4E4] py-3 pl-10 pr-4 text-sm focus:border-[#C24914] focus:outline-none focus:ring-2 focus:ring-[#C24914]/20"
                    placeholder="voce@exemplo.com"
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label htmlFor="password" className="block text-sm font-medium text-[#323338]">Senha</label>
                  <Link to="/forgot-password" className="text-sm font-medium text-[#C24914] hover:underline">
                    Esqueceu sua senha?
                  </Link>
                </div>
                <div className="relative">
                  <Lock size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#676879]" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="w-full rounded-lg border border-[#D0D4E4] py-3 pl-10 pr-12 text-sm focus:border-[#C24914] focus:outline-none focus:ring-2 focus:ring-[#C24914]/20"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#676879] hover:text-[#323338]"
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button type="submit" variant="brand" loading={loading} className="w-full justify-center py-3">
                <span className="inline-flex items-center gap-2">
                  Entrar
                  <ArrowRight size={16} />
                </span>
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-[#676879]">
              O acesso é fornecido pelo administrador da sua empresa.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
